import Alert from "@mui/material/Alert";
import dayjs from "dayjs";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { UserLink } from "@/shared/components";
import { FileMetadataAndTopics } from "@/shared/components/FileMetadataAndTopics";
import {
  ResourceView,
  activeFiltersToQuerySubmission,
  useResourceView,
  type FilterDefinition,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { TagList } from "@/shared/components/TagList";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord } from "@/shared/domain/files";
import {
  filesScopeToCondition,
  type FilesQueryScope,
} from "@/shared/domain/files/FilesQueryScope";
import {
  fileQueryKeys,
  useFileMetadataKeys,
} from "@/shared/domain/files/hooks";
import {
  composeQueryBody,
  formatMetadataValue,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
} from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useFileTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation/Goto";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";

import styles from "./FilesList.module.css";

const getFileId: EntityIdGetter<FileRecord> = (f) => f.file_id;

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Files };

const columnHelper = createColumnHelper<FileRecord>();

function formatRelativeOrShort(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const parsed = dayjs(value);
  return dayjs().diff(parsed, "day") >= 7
    ? parsed.format("MMM D, YYYY")
    : parsed.fromNow();
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === undefined || bytes === null) {
    return "";
  }
  if (bytes === 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  return `${value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[exponent]}`;
}

function PrincipalCell(params: ICellRendererParams<FileRecord, string>) {
  const value = params.value ?? "";
  if (!value) {
    return null;
  }
  const principal = RobotoPrincipal.fromString(value);
  if (principal?.ptype === RobotoPrincipalType.User) {
    return (
      <UserLink
        userId={principal.unqualifiedId}
        linkSx={{ fontSize: "var(--font-size-xs)" }}
      />
    );
  }
  return <span>{principal?.unqualifiedId ?? value}</span>;
}

const STATIC_FILE_COLUMNS: ColDef<FileRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: false,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const fileId = p.data?.file_id;
      const name = p.value ?? "";
      if (!fileId) {
        return name;
      }
      return (
        <Link to={`${Goto.route.Files}/${fileId}`} className={styles.nameLink}>
          {name}
        </Link>
      );
    },
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 160,
    sortable: true,
    valueFormatter: (p) => formatRelativeOrShort(p.value),
  }),
  {
    colId: "source",
    field: "origination",
    headerName: "Source",
    width: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (value.startsWith("iv_")) {
        return (
          <Link
            to={`${Goto.route.Invocations}/${value}`}
            className={styles.nameLink}
          >
            {value}
          </Link>
        );
      }
      return value;
    },
  },
  columnHelper.accessor("association_id", {
    headerName: "Association ID",
    width: 200,
    sortable: false,
    cellClass: cellStyles.monoCell,
    cellRenderer: (p: ICellRendererParams<FileRecord, string>) => {
      const value = p.value ?? "";
      if (!value) {
        return "";
      }
      if (p.data?.association_type === AssociationType.Dataset) {
        return (
          <Link
            to={`${Goto.route.Datasets}/${value}`}
            className={styles.nameLink}
          >
            {value}
          </Link>
        );
      }
      return value;
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: (p: ICellRendererParams<FileRecord, string[]>) => (
      <TagList tags={p.value ?? []} maxDisplayCount={3} />
    ),
  }),
  columnHelper.accessor("file_id", {
    headerName: "File ID",
    width: 180,
    sortable: false,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 150,
    sortable: false,
    cellRenderer: PrincipalCell,
  }),
  columnHelper.accessor("modified", {
    headerName: "Modified",
    width: 160,
    sortable: true,
    valueFormatter: (p) => formatRelativeOrShort(p.value),
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    width: 150,
    sortable: false,
    cellRenderer: PrincipalCell,
  }),
  columnHelper.accessor("relative_path", {
    headerName: "Path",
    flex: 2,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("size", {
    headerName: "Size",
    width: 100,
    sortable: false,
    valueFormatter: (p) => formatBytes(p.value),
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org ID",
    width: 160,
    sortable: false,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("uri", {
    headerName: "URI",
    flex: 2,
    sortable: false,
    cellClass: "truncate",
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    width: 110,
    sortable: false,
  }),
  columnHelper.accessor("upload_id", {
    headerName: "Upload ID",
    width: 180,
    sortable: false,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("device_id", {
    headerName: "Device ID",
    width: 160,
    sortable: false,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 2,
    sortable: false,
    valueFormatter: (p) => p.value ?? "",
  }),
  columnHelper.accessor("ingestion_status", {
    headerName: "Ingestion Status",
    width: 140,
    sortable: false,
  }),
  columnHelper.accessor("version", {
    headerName: "Version",
    width: 90,
    sortable: false,
  }),
  columnHelper.accessor("fs_type", {
    headerName: "FS Type",
    width: 100,
    sortable: false,
  }),
  columnHelper.accessor("parent_id", {
    headerName: "Parent ID",
    width: 180,
    sortable: false,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("storage_type", {
    headerName: "Storage Type",
    width: 130,
    sortable: false,
  }),
];

const DEFAULT_VISIBLE_IDS = [
  "name",
  "created",
  "source",
  "association_id",
  "tags",
];

const STATIC_PICKER_OPTIONS: ColumnPickerOption[] = STATIC_FILE_COLUMNS.map(
  (c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }),
);

function buildMetadataColumn(metadataKey: string): ColDef<FileRecord> {
  return {
    colId: `metadata.${metadataKey}`,
    headerName: metadataKey,
    valueGetter: (p) => p.data?.metadata?.[metadataKey],
    valueFormatter: (p) => formatMetadataValue(p.value),
    sortable: false,
  };
}

export interface FilesListProps {
  scope: FilesQueryScope;
  /** Bulk-action options surfaced in the DataTable.Selection menu. */
  selectionOptions?: SelectionOption<FileRecord>[];
}

export function FilesList({ scope, selectionOptions = [] }: FilesListProps) {
  const { currentOrganization } = useAuth();
  const { files: fileService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useFileTablePreferences();

  const metadataKeysQuery = useFileMetadataKeys(resolvedOrgId);

  const [initialColumnIds] = React.useState(() => preferences.columns);

  const columns = React.useMemo<ColDef<FileRecord>[]>(
    () =>
      buildColumnsForPreferences<FileRecord>({
        staticColumns: STATIC_FILE_COLUMNS,
        dynamicColumns: (metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        preferenceOrder: initialColumnIds,
      }),
    [initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_PICKER_OPTIONS,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [metadataKeysQuery.data],
  );

  const view = useResourceView<FileRecord>({
    getRowId: getFileId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  // Filter definitions are restricted to fields the backend exposes via
  // `FILE_FIELDS` in `roboql/model/core.py`. Excluded:
  //   - parent_id, version, association_type, ingestion_status, fs_type,
  //     storage_type: not in FILE_FIELDS (backend rejects the condition).
  //   - status: we only surface "available" files, not "reserved" or "deleted",
  //     so this filter would buy us nothing.
  //   - org_id: always implicitly scoped to the current org by the
  //     structured-query backend.
  //   - metadata: dynamic, handled by Milestone 4.
  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "file_id", name: "File Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "relative_path", name: "Relative Path" },
      { type: "string", field: "description", name: "Description" },
      { type: "string", field: "uri", name: "URI" },
      { type: "string", field: "device_id", name: "Device Id" },
      { type: "string", field: "origination", name: "Source" },
      { type: "string", field: "upload_id", name: "Upload Id" },
      { type: "string", field: "association_id", name: "Association Id" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      { type: "numeric", field: "size", name: "Size" },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: function getFileTags() {
          return fileService.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
    ],
    [fileService, resolvedOrgId],
  );

  const submission = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
  });

  const query = composeQueryBody({
    scopeCondition: filesScopeToCondition(scope),
    filterCondition: submission.filterCondition,
    sort: submission.sort,
    maxResults: submission.maxResults,
  });

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Files,
    contentMode: QueryContentMode.RecordWithMeta,
    queryKey: fileQueryKeys.list(resolvedOrgId, scope, query),
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<FileRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const rows: FileRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const renderExpandedRow = React.useCallback((file: FileRecord) => {
    return (
      <FileMetadataAndTopics
        fileId={file.file_id}
        fileVersion={file.version}
        resourceOwnerId={file.org_id}
        readonly
      />
    );
  }, []);

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = selectionOptions.length > 0;

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {hasSelectionOptions && (
            <ResourceView.Selection<FileRecord>
              selection={view.table.selection}
              clearSelection={view.table.clearSelection}
              options={selectionOptions}
            />
          )}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <DataTable<FileRecord>
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No files"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </ResourceView.Root>
    </>
  );
}
